// ✅ src/pages/OrderManagement/ProjectRABillsdoc.jsx  (FULL FILE)
// Works with backend mount:
//   app.use("/api/projectRABillsDoc", projectRABillsDocRouter);
//
// Endpoints expected:
//   GET  http://nissi-india.com/api/projectRABillsDoc/list/:contractId
//   POST http://nissi-india.com/api/projectRABillsDoc/upload
//   POST http://nissi-india.com/api/projectRABillsDoc/submit

import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  FaUpload,
  FaPlus,
  FaTrash,
  FaSave,
  FaEye,
  FaDownload,
  FaArrowLeft,
  FaFileAlt,
} from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API = "http://nissi-india.com/api/projectRABillsDoc";

const toInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const niceBytes = (bytes) => {
  const b = Number(bytes || 0);
  if (!b) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let x = b;
  while (x >= 1024 && i < units.length - 1) {
    x /= 1024;
    i++;
  }
  return `${x.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

export default function ProjectRABillsdoc() {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ localStorage helper
  const getLS = (key) => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem(key) || "";
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchId = useMemo(() => toInt(branchIdLS || 0), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "").trim(),
    [branchTypeLS]
  );
  const loginUserId = useMemo(() => toInt(userIdLS || 0), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  const qs = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const contractId = useMemo(
    () => toInt(qs.get("contractId") || qs.get("contractid")),
    [qs]
  );

  const [loading, setLoading] = useState(false);

  // { fileName, url, source:"db"|"new" }
  const [files, setFiles] = useState([]);
  const [pickedFile, setPickedFile] = useState(null);
  const pickRef = useRef(null);

  const load = async () => {
    if (!contractId) {
      toast.error("contractId missing in URL");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API}/list/${contractId}`, {
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "View failed");
        return;
      }

      const list = (res.data?.data || []).map((x) => ({
        fileName: x.fileName,
        url: `http://nissi-india.com${x.url}`,
        source: "db",
      }));

      setFiles(list);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  const onPick = (e) => {
    const f = e.target.files?.[0] || null;
    setPickedFile(f);
  };

  const uploadAndAdd = async () => {
    if (!contractId) return toast.error("contractId missing");
    if (!loginUserId) return toast.error("loginUserId missing in localStorage");
    if (!pickedFile) return toast.error("Please choose a file first");

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", pickedFile);

      const res = await axios.post(`${API}/upload`, fd, {
        headers: {
          ...commonHeaders,
          "Content-Type": "multipart/form-data",
        },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Upload failed");
        return;
      }

      const fileName = res.data.fileName;
      const url = `http://nissi-india.com${res.data.url}`;

      setFiles((prev) => {
        if (prev.some((p) => p.fileName === fileName)) return prev;
        return [...prev, { fileName, url, source: "new" }];
      });

      toast.success("Added to list");
      setPickedFile(null);
      if (pickRef.current) pickRef.current.value = "";
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const removeItem = (fileName) => {
    setFiles((prev) => prev.filter((x) => x.fileName !== fileName));
  };

  const submit = async () => {
    if (!contractId) return toast.error("contractId missing");
    if (!loginUserId) return toast.error("loginUserId missing in localStorage");
    if (!files.length) return toast.error("Please add at least 1 file");

    setLoading(true);
    try {
      const payload = {
        contractId,
        loginUserId,
        files: files.map((x) => x.fileName),
      };

      const res = await axios.post(`${API}/submit`, payload, {
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Save failed");
        return;
      }

      toast.success(res.data?.message || "Saved Successfully");
      await load();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const openFile = (url) => window.open(url, "_blank", "noopener,noreferrer");

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50">
      <ToastContainer position="top-right" autoClose={2500} />

      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 flex items-center gap-2"
            >
              <FaArrowLeft /> Back
            </button>

            <div>
              <div className="text-lg font-extrabold text-slate-900 flex items-center gap-2">
                <FaFileAlt className="text-indigo-600" />
                Project RA Bills Documents
              </div>
              <div className="text-xs text-slate-600">
                Contract ID: <b>{contractId || "-"}</b>
              </div>
            </div>
          </div>

          <button
            disabled={loading}
            onClick={submit}
            className={`px-4 py-2 rounded-lg text-white flex items-center gap-2 ${
              loading
                ? "bg-emerald-600/60 cursor-not-allowed"
                : "bg-emerald-600 hover:bg-emerald-700"
            }`}
          >
            <FaSave /> Submit
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 md:p-6">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="text-xl font-bold text-slate-900">Upload & Add Documents</div>
              <div className="text-sm text-slate-600">
                Choose a file → click <b>Add</b> → then click <b>Submit</b> to save list.
              </div>
            </div>

            <div className="flex gap-2">
              <button
                disabled={loading}
                onClick={() => pickRef.current?.click()}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2"
              >
                <FaUpload /> Choose File
              </button>

              <button
                disabled={loading}
                onClick={uploadAndAdd}
                className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white flex items-center gap-2"
              >
                <FaPlus /> Add
              </button>
            </div>
          </div>

          <div className="mt-4">
            <input
              ref={pickRef}
              type="file"
              onChange={onPick}
              className="hidden"
              accept="*/*"
            />

            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <div className="flex-1 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3">
                <div className="text-sm text-slate-700">
                  {pickedFile ? (
                    <>
                      Selected: <b>{pickedFile.name}</b>{" "}
                      <span className="text-slate-500">
                        ({niceBytes(pickedFile.size)})
                      </span>
                    </>
                  ) : (
                    "No file selected"
                  )}
                </div>
              </div>

              <button
                disabled={loading}
                onClick={load}
                className="px-4 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="text-lg font-bold text-slate-900">Documents</div>
              <div className="text-xs text-slate-600">
                Click <b>View</b> to open, or <b>Download</b> to download.
              </div>
            </div>

            <div className="text-sm text-slate-600">
              Total: <b>{files.length}</b>
            </div>
          </div>

          <div className="divide-y divide-slate-200">
            {files.length === 0 ? (
              <div className="p-6 text-slate-600">No documents added.</div>
            ) : (
              files.map((f) => (
                <div
                  key={f.fileName}
                  className="p-4 md:p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{f.fileName}</div>
                    <div className="text-xs text-slate-600">
                      Source:{" "}
                      <span
                        className={
                          f.source === "new"
                            ? "text-indigo-700 font-semibold"
                            : "text-slate-700"
                        }
                      >
                        {f.source === "new" ? "Added (not saved yet)" : "Saved"}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => openFile(f.url)}
                      className="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2"
                    >
                      <FaEye /> View
                    </button>

                    <a
                      href={f.url}
                      download
                      className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2"
                    >
                      <FaDownload /> Download
                    </a>

                    <button
                      onClick={() => removeItem(f.fileName)}
                      className="px-3 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white flex items-center gap-2"
                      title="Remove from list"
                    >
                      <FaTrash /> Remove
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="p-4 md:p-5 bg-slate-50 border-t border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-sm text-slate-700">
              Tip: Upload + Add multiple files, then click <b>Submit</b> once.
            </div>

            <button
              disabled={loading || files.length === 0}
              onClick={submit}
              className={`px-5 py-2 rounded-lg text-white flex items-center gap-2 ${
                loading || files.length === 0
                  ? "bg-emerald-600/60 cursor-not-allowed"
                  : "bg-emerald-600 hover:bg-emerald-700"
              }`}
            >
              <FaSave /> Submit & Save List
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}